import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProfile } from '../api/profile';
import Button from '../components/Button';
import styles from './Payment.module.css';

const AMOUNT = 2000;
const PUBLIC_ID = process.env.REACT_APP_CLOUDPAYMENTS_PUBLIC_ID || 'pk_your_public_id_here';

const Payment = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [payError, setPayError] = useState('');

  useEffect(() => {
    document.title = 'Оплата подписки — Терушкин ОС';
  }, []);

  // Если не авторизован — редирект на логин
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Загрузка профиля
  useEffect(() => {
    if (!token) return;
    getProfile(token)
      .then((data) => {
        setProfile(data);
        const payUntil = data.pay_until ? new Date(data.pay_until) : null;
        setIsPaid(payUntil !== null && payUntil > new Date());
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [token]);

  // Редирект после успешной оплаты
  useEffect(() => {
    if (paySuccess) {
      const timer = setTimeout(() => navigate('/news'), 3000);
      return () => clearTimeout(timer);
    }
  }, [paySuccess, navigate]);

  const handlePay = () => {
    if (!window.cp) {
      setPayError('Платёжный виджет не загружен. Попробуйте обновить страницу.');
      return;
    }

    setPayError('');

    const widget = new window.cp.CloudPayments();
    widget.pay('charge', {
      publicId: PUBLIC_ID,
      description: 'Подписка на клуб Терушкин ОС (1 месяц)',
      amount: AMOUNT,
      currency: 'RUB',
      accountId: profile.id,
      email: profile.email,
      data: {
        CloudPayments: {
          recurrent: {
            interval: 'Month',
            period: 1,
          },
        },
      },
    }, {
      onSuccess: () => {
        setPaySuccess(true);
      },
      onFail: (reason) => {
        setPayError(reason || 'Оплата не прошла. Попробуйте ещё раз или используйте другую карту.');
      },
    });
  };

  if (!isAuthenticated) return null;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <p className={styles.loading}>Загрузка...</p>
        </div>
      </div>
    );
  }

  // Успешная оплата
  if (paySuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.successIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className={styles.title}>Подписка оформлена!</h1>
          <p className={styles.message}>
            Спасибо за оплату. Вы получили доступ ко всем материалам клуба.
            Перенаправляем вас в новости...
          </p>
          <Link to="/news">
            <Button variant="primary" size="md">Перейти в новости</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Уже оплачено
  if (isPaid) {
    const payUntilDate = new Date(profile.pay_until).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.activeIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <h1 className={styles.title}>Подписка активна</h1>
          <p className={styles.message}>
            Ваша подписка действует до <strong>{payUntilDate}</strong>.
            Следующее списание произойдёт автоматически.
          </p>
          <Link to="/news">
            <Button variant="primary" size="md">Перейти в новости</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Форма оплаты
  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <h1 className={styles.title}>Оформление подписки</h1>
        <p className={styles.subtitle}>
          Получите доступ к закрытым лекциям, вебинарам и персональным рекомендациям от ведущих специалистов клуба
        </p>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardPlan}>Базовый</span>
            <div className={styles.cardPrice}>
              <span className={styles.priceAmount}>{AMOUNT.toLocaleString('ru-RU')} ₽</span>
              <span className={styles.pricePeriod}>/ месяц</span>
            </div>
          </div>
          <ul className={styles.cardFeatures}>
            <li>Закрытые лекции и вебинары (2 в месяц)</li>
            <li>Ответы на вопросы от Терушкина Р.А.</li>
            <li>Закрытое сообщество единомышленников</li>
            <li>Скидки на анализы и диагностику</li>
            <li>Ежемесячные отчёты о здоровье</li>
          </ul>
          <Button variant="primary" size="lg" className={styles.payButton} onClick={handlePay}>
            Оплатить {AMOUNT.toLocaleString('ru-RU')} ₽
          </Button>
          <p className={styles.cardNote}>
            Подписка продлевается автоматически. Отменить можно в любой момент.
          </p>
        </div>

        {payError && <p className={styles.error}>{payError}</p>}

        <button
          onClick={() => navigate(-1)}
          className={styles.back}
        >
          ← Назад
        </button>
      </div>
    </div>
  );
};

export default Payment;
